/**
 * Der Filterzustand wird in der Adresszeile geführt. Dadurch funktionieren
 * Zurück-Taste, Lesezeichen und das Teilen innerhalb des Haushalts.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EMPTY_FILTER } from '../lib/search.ts';
import type { RecipeFilter, SortKey } from '../lib/search.ts';

/** Liest einen Wert aus der Adresszeile. */
function readValue(params: URLSearchParams, key: string): string {
  return params.get(key) ?? '';
}

/** Zerlegt eine mit Komma getrennte Liste. */
function readList(params: URLSearchParams, key: string): string[] {
  const raw = readValue(params, key);
  return raw === '' ? [] : raw.split(',').filter((entry) => entry !== '');
}

/** Zerlegt eine Zahlenangabe. */
function readNumber(params: URLSearchParams, key: string): number {
  const value = Number.parseInt(readValue(params, key), 10);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

const SORT_KEYS: readonly SortKey[] = [
  'relevance',
  'rating',
  'totalTime',
  'activeTime',
  'newest',
  'lastCooked',
  'title',
];

/** Wandelt die Adresszeile in einen Filter um. */
export function filterFromParams(params: URLSearchParams): RecipeFilter {
  const sort = readValue(params, 'sort');
  const cooked = readValue(params, 'seit');
  const days = Number.parseInt(cooked, 10);
  const cookedBefore =
    Number.isFinite(days) && days > 0
      ? new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString()
      : null;

  return {
    query: readValue(params, 'q'),
    categoryIds: readList(params, 'kategorie'),
    tagNames: readList(params, 'tag'),
    tagsAll: readValue(params, 'tagModus') === 'alle',
    difficulties: readList(params, 'schwierigkeit').filter(
      (value): value is 'easy' | 'medium' | 'hard' =>
        value === 'easy' || value === 'medium' || value === 'hard',
    ),
    maxTotalMinutes: readNumber(params, 'zeit'),
    maxActiveMinutes: readNumber(params, 'aktivzeit'),
    minRating: readNumber(params, 'sterne'),
    ratingByMe: readValue(params, 'sterneVon') === 'mir',
    unratedOnly: readValue(params, 'unbewertet') === 'ja',
    favoritesOnly: readValue(params, 'merkliste') === 'ja',
    cookedBefore,
    withIngredients: readList(params, 'mit'),
    withoutIngredients: readList(params, 'ohne'),
    sort: SORT_KEYS.includes(sort as SortKey) ? (sort as SortKey) : 'newest',
  };
}

/** Wandelt einen Filter in Adressparameter um. */
export function paramsFromFilter(filter: RecipeFilter): URLSearchParams {
  const params = new URLSearchParams();
  if (filter.query !== '') {
    params.set('q', filter.query);
  }
  if (filter.categoryIds.length > 0) {
    params.set('kategorie', filter.categoryIds.join(','));
  }
  if (filter.tagNames.length > 0) {
    params.set('tag', filter.tagNames.join(','));
  }
  if (filter.tagsAll) {
    params.set('tagModus', 'alle');
  }
  if (filter.difficulties.length > 0) {
    params.set('schwierigkeit', filter.difficulties.join(','));
  }
  if (filter.maxTotalMinutes > 0) {
    params.set('zeit', String(filter.maxTotalMinutes));
  }
  if (filter.maxActiveMinutes > 0) {
    params.set('aktivzeit', String(filter.maxActiveMinutes));
  }
  if (filter.minRating > 0) {
    params.set('sterne', String(filter.minRating));
  }
  if (filter.ratingByMe) {
    params.set('sterneVon', 'mir');
  }
  if (filter.unratedOnly) {
    params.set('unbewertet', 'ja');
  }
  if (filter.favoritesOnly) {
    params.set('merkliste', 'ja');
  }
  if (filter.cookedBefore !== null) {
    // Der Filter wird als "Tage" gespeichert, damit die Adresse kurz bleibt.
    const days = Math.max(1, Math.round((Date.now() - Date.parse(filter.cookedBefore)) / 86_400_000));
    params.set('seit', String(days));
  }
  if (filter.withIngredients.length > 0) {
    params.set('mit', filter.withIngredients.join(','));
  }
  if (filter.withoutIngredients.length > 0) {
    params.set('ohne', filter.withoutIngredients.join(','));
  }
  if (filter.sort !== 'newest') {
    params.set('sort', filter.sort);
  }
  return params;
}
/** Filter mit der Adresszeile als Speicher. */
export function useRecipeFilter(): {
  readonly filter: RecipeFilter;
  readonly setFilter: (next: RecipeFilter) => void;
  readonly reset: () => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = useMemo(() => filterFromParams(searchParams), [searchParams]);

  const setFilter = useCallback(
    (next: RecipeFilter) => {
      setSearchParams(paramsFromFilter(next), { replace: true });
    },
    [setSearchParams],
  );

  const reset = useCallback(() => {
    setSearchParams(paramsFromFilter(EMPTY_FILTER), { replace: true });
  }, [setSearchParams]);

  return { filter, setFilter, reset };
}
